import React, {useCallback, useEffect, useMemo, useState} from "react";
import {usePost} from "@/apiHooks/HttpFunctions";
import RenderToast from "@/components/RenderToast";
import {Col, Row} from "react-bootstrap";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import {useFormik} from "formik";
import getUserMutationSchema from "@/Modals/WebUserMutation/schema";
import classes from "@/Modals/BaseModal.module.css";
import Input from "@/components/Input/Input";
import useFormApiState from "@/CustomHooks/useFormApiState";


function WebUserMutationModal({
  headerText,
  userId,
  isOpen,
  endpointUrl,
  onHide,
  onSuccess,
  requireReason = true,
  allowReference = true,
  allowAttachments = true,
  ...props
}) {
  const initialState = useMemo(() => ({
    user_id: 0,
    mutation_type: '',
    reason: '',
    reference_id: '',
    attachments: [],
  }), []);
  const [formData, setFormData] = useState(initialState);

  useEffect(() => {
    setFormData((prevData) => ({...prevData, user_id: userId}));
  }, [userId]);

  const onSubmitSuccess = useCallback((message, callback = null) => {
    setFormData((prev) => ({...initialState, user_id: userId}));
    RenderToast({type: "success", message});
    if (onHide) {
      onHide();
    }
    if (callback) {
      callback();
    }
  }, [initialState, onHide, userId]);

  const userMutationPost = usePost({
    apiURL: endpointUrl,
    isFormData: true,
    onSuccess: (data) => onSubmitSuccess(data.message, onSuccess)
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [userMutationPost.isPending],
    [userMutationPost.error]
  );

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("user_id", values.user_id);
    formData.append("reason", values.reason);
    formData.append("reference_id", values.reference_id);
    for (let file of values["attachments"]) {
      formData.append("attachments[]", file);
    }
    userMutationPost.mutate(formData);
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: getUserMutationSchema(requireReason),
    initialValues: formData,
    onSubmit: handleSubmit,
  });

  const onFileChange = useCallback((e) => {
    formik.setFieldValue("attachments", e.target.files);
  }, [formik]);

  return (
    <BasicModal
      header={headerText}
      show={isOpen}
      onHide={onHide}
      saveLoading={saveIsPending}
      formik={formik}
      saveLabel={"Proceed"}
    >
      <Row>
        <Col md={12}>
          <p className="mb-1 font-bold">Please enter details for this action</p>
        </Col>
      </Row>
      {requireReason && <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"reason"}
            saveError={saveError}
            label="Reason*"
            placeholder="Enter reason"
            type="textarea"
            rows={2}
          />
        </Col>
      </Row>}
      {allowReference && <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"reference_id"}
            saveError={saveError}
            label="Reference ID"
          />
        </Col>
      </Row>}
      {allowAttachments && <Row>
        <Col md={12}>
          <Input
            type="file"
            value={formik.values.attachments}
            onChange={onFileChange}
            labelClass={classes.label}
            label="Attachments"
            className={classes.inputDiv}
            inputContainerClass={classes.input}
            errorTextClass={classes.errorText}
            multiple
            accept="image/*, application/pdf"
          />
        </Col>
      </Row>}
    </BasicModal>
  );
}

export default WebUserMutationModal;
